// deps
import { useCallback, useState } from "react";

// utils
import functionNoop from "../utils/function/noop";

/**
 * @callback OnOpenChange
 * @param {object} change
 * @param {boolean} change.open
 */

/**
 * @typedef {object} UseDisclosureReturn
 * @prop {boolean} open
 * @prop {() => void} onOpen
 * @prop {() => void} onClose
 * @prop {() => void} onToggle
 * @prop {OnOpenChange} onOpenChange
 * @prop {import("react").Dispatch<import("react").SetStateAction<boolean>>} setOpen
 */

/**
 * @typedef {object} UseDisclosureOptions
 * @prop {() => void} [onOpen]
 * @prop {() => void} [onClose]
 */

/**
 * Hook de gestion de l’état d'ouverture d’un élément.
 * @param {boolean} [initialOpen]
 * @param {UseDisclosureOptions} [options]
 * @returns {UseDisclosureReturn}
 */
export default function useDisclosure(initialOpen = false, options = {}) {
  const [open, setOpen] = useState(initialOpen);

  const {
    onOpen: userOnOpen = functionNoop,
    onClose: userOnClose = functionNoop,
  } = options;

  const handleOpen = useCallback(
    /**
     * @param {...any} attrs
     */
    function (...attrs) {
      setOpen(true);
      userOnOpen(attrs);
    },
    [userOnOpen],
  );

  const handleClose = useCallback(
    /**
     * @param {...any} attrs
     */
    function (...attrs) {
      setOpen(false);
      userOnClose(...attrs);
    },
    [userOnClose],
  );

  const handleToggle = useCallback(
    /**
     * @param {...any} attrs
     */
    function (...attrs) {
      if (open) {
        handleClose(attrs);
      } else {
        handleOpen(attrs);
      }
    },
    [handleOpen, handleClose, open],
  );

  const handleOpenChange = useCallback(
    /**
     * @param {object} change
     * @param {boolean} change.open
     */
    function (change) {
      if (change.open) {
        handleOpen();
      } else {
        handleClose();
      }
    },
    [handleOpen, handleClose],
  );

  return {
    open,
    setOpen,
    onOpen: handleOpen,
    onClose: handleClose,
    onToggle: handleToggle,
    onOpenChange: handleOpenChange,
  };
}
